import type { TokenOptimizationSummary } from '@ace-tracker/shared';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartAxisTickProps, chartTooltipProps } from '../chartTheme';
import { Button } from './Button';
import { ChartCard } from './ChartCard';
import { EfficiencyGauge, MiniGauge, PERCENT_BANDS, type GaugeBand } from './EfficiencyGauge';
import { CONTEXT_REDUCTION_BANDS, graphifyContextReductionPercent } from './GraphifyDashboardBody';
import { compactNumber, ReadingGuide, wholeNumber, type SectionHeadingProps } from './OptimizationShared';
import { RTK_REDUCTION_BANDS } from './RtkDashboardBody';

export type OptimizerView = 'rtk' | 'headroom' | 'graphify';

const OPTIMIZER_LABELS: Record<OptimizerView, string> = { rtk: 'RTK', headroom: 'Headroom', graphify: 'Graphify' };
const OPTIMIZER_COLORS: Record<OptimizerView, string> = { rtk: '#0058AB', headroom: '#06D6A0', graphify: '#C77DFF' };
const OPTIMIZER_ROLES: Record<OptimizerView, string> = {
  rtk: 'Filters shell-command output',
  headroom: 'Compresses model context',
  graphify: 'Graph queries instead of whole files',
};

/**
 * The optimizer overview — the counterpart of the "All tools" view for RTK,
 * Headroom, and Graphify. Each optimizer gets a summary card linking to its
 * own screen; the only cross-optimizer comparison is how many developers
 * report each one. Their token figures are never added or charted together:
 * RTK estimates shell output, Headroom counts compressed context, and
 * Graphify's benchmark is modeled (see dashboard/AGENTS.md).
 */
export function TokenOptimizationDashboardBody({
  summary,
  SectionHeading,
  onSelectOptimizer,
}: {
  summary: TokenOptimizationSummary;
  SectionHeading: (props: SectionHeadingProps) => JSX.Element;
  onSelectOptimizer: (view: OptimizerView) => void;
}) {
  const coverage = summary.coverage;
  const rtk = summary.rtkTotals;
  const rtkReporting = summary.developers.length > 0;
  // Headroom's widest window with data is its most representative figure.
  const headroom = summary.headroomTotals[summary.headroomTotals.length - 1];
  const headroomDevelopers = new Set(summary.headroom.filter((row) => row.devices > 0).map((row) => row.developerId)).size;
  const graphify = summary.graphifyTotals;
  const graphifyReduction = graphify ? graphifyContextReductionPercent(graphify.averageCorpusTokens, graphify.averageQueryTokens) : null;

  const reportingChart = (['rtk', 'headroom', 'graphify'] as const).map((tool) => ({
    tool,
    label: OPTIMIZER_LABELS[tool],
    developers: tool === 'rtk' ? summary.developers.length : tool === 'headroom' ? headroomDevelopers : summary.graphify.length,
  }));

  return (
    <>
      <ReadingGuide>
        RTK, Headroom, and Graphify are three independent, optional tools with different measurement methods — RTK estimates shell
        output it filtered, Headroom counts context it compressed, and Graphify models how much smaller a graph query is than the whole
        corpus. Each card links to that tool's own screen. Their token figures are never added together or compared directly.
      </ReadingGuide>

      <section aria-labelledby="coverage-heading">
        <div id="coverage-heading">
          <SectionHeading
            title="Coverage"
            hint="Active AI developers who also report each optimizer"
            info="Active AI developers: developers with at least one Copilot, Claude Code, Codex, Kiro, Gemini CLI, or Cursor request in the selected range (the Executive view's definition). Each meter shows how many of them also reported that optimizer in the same range. A developer counts once however many machines they use."
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(['rtk', 'headroom', 'graphify'] as const).map((tool) => {
            const reporting = coverage[tool];
            const active = coverage.activeAiDevelopers;
            const coveragePercent = active > 0 ? (reporting / active) * 100 : null;
            return (
              <EfficiencyGauge
                key={tool}
                label={`${OPTIMIZER_LABELS[tool]} coverage`}
                value={coveragePercent}
                bands={PERCENT_BANDS}
                valueLabel={coveragePercent === null ? undefined : `${Math.round(coveragePercent)}%`}
                meta={active > 0 ? `${reporting} of ${active} active AI developers` : 'No active AI developers in this range'}
                size="sm"
              />
            );
          })}
        </div>
      </section>

      <section aria-labelledby="optimizers-heading">
        <div id="optimizers-heading">
          <SectionHeading title="At a glance" hint="Each optimizer on its own terms — open a card for its full screen" />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <OptimizerCard
            tool="rtk"
            reporting={rtkReporting}
            onSelect={onSelectOptimizer}
            gauge={{ label: 'Estimated reduction', value: rtk.estimatedRawOutputTokens > 0 ? rtk.reductionPercent : null, bands: RTK_REDUCTION_BANDS }}
            metrics={[
              { label: 'Est. tokens saved', value: compactNumber(rtk.estimatedSavedTokens) },
              { label: 'Reduction', value: `${rtk.reductionPercent.toFixed(1)}%` },
              { label: 'Commands', value: compactNumber(rtk.commands) },
              { label: 'Developers', value: wholeNumber(summary.developers.length) },
            ]}
            footnote="Estimated from shell-output bytes ÷ 4 over the selected range."
          />
          <OptimizerCard
            tool="headroom"
            reporting={Boolean(headroom)}
            onSelect={onSelectOptimizer}
            gauge={{ label: 'Savings', value: headroom && headroom.tokensBefore > 0 ? headroom.savingsPercent : null, bands: PERCENT_BANDS }}
            metrics={
              headroom
                ? [
                    { label: 'Tokens saved', value: compactNumber(headroom.tokensSaved) },
                    { label: 'Savings', value: `${headroom.savingsPercent.toFixed(1)}%` },
                    { label: 'Compressions', value: compactNumber(headroom.calls) },
                    { label: 'Developers', value: wholeNumber(headroomDevelopers) },
                  ]
                : []
            }
            footnote={headroom ? `Rolling ${headroom.window.replaceAll('_', ' ')} window${summary.headroomAsOfDate ? ` · as of ${summary.headroomAsOfDate}` : ''}.` : undefined}
          />
          <OptimizerCard
            tool="graphify"
            reporting={Boolean(graphify)}
            onSelect={onSelectOptimizer}
            gauge={{ label: 'Modeled context reduction', value: graphify && graphify.benchmarkedRoots > 0 ? graphifyReduction : null, bands: CONTEXT_REDUCTION_BANDS }}
            metrics={
              graphify
                ? [
                    { label: 'Build tokens', value: compactNumber(graphify.buildInputTokens + graphify.buildOutputTokens) },
                    { label: 'Reduction ratio', value: graphify.benchmarkedRoots > 0 ? `${graphify.averageReductionRatio.toFixed(1)}×` : '—' },
                    { label: 'Benchmarked roots', value: wholeNumber(graphify.benchmarkedRoots) },
                    { label: 'Developers', value: wholeNumber(graphify.developers) },
                  ]
                : []
            }
            footnote={graphify ? `Build tokens measured; benchmark modeled${summary.graphifyAsOfDate ? ` · as of ${summary.graphifyAsOfDate}` : ''}.` : undefined}
          />
        </div>
      </section>

      <section aria-labelledby="comparison-heading">
        <div id="comparison-heading">
          <SectionHeading title="Side-by-side" hint="Only developer counts are compared across optimizers" />
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ChartCard title="Developers reporting each optimizer" caption="Distinct developers with at least one report in the selected range." height="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={reportingChart} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis type="number" allowDecimals={false} {...chartAxisTickProps} />
                <YAxis type="category" dataKey="label" width={90} {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} formatter={(value) => [wholeNumber(Number(value)), 'Developers']} />
                <Bar dataKey="developers" name="Developers">
                  {reportingChart.map((entry) => (
                    <Cell key={entry.tool} fill={OPTIMIZER_COLORS[entry.tool]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <div className="rounded-card border border-border bg-surface p-5 text-sm shadow-sm">
            <h3 className="font-medium text-text-default">Why token figures aren't compared here</h3>
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-text-muted">
              <li>RTK's savings are estimates of shell output bytes ÷ 4, before anything reaches a model.</li>
              <li>Headroom's savings are context tokens its proxy and MCP paths compressed, over rolling windows.</li>
              <li>Graphify's benchmark is modeled from sample questions; only its build cost is measured.</li>
            </ul>
            <p className="mt-3 text-xs text-text-faint">Adding them would double-count and mix estimates with models — open each screen for its own detail.</p>
          </div>
        </div>
      </section>
    </>
  );
}

function OptimizerCard({
  tool,
  reporting,
  gauge,
  metrics,
  footnote,
  onSelect,
}: {
  tool: OptimizerView;
  reporting: boolean;
  gauge: { label: string; value: number | null; bands: GaugeBand[] };
  metrics: { label: string; value: string }[];
  footnote?: string;
  onSelect: (view: OptimizerView) => void;
}) {
  return (
    <div className="relative flex flex-col rounded-card border border-border bg-surface p-5 shadow-sm">
      <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: OPTIMIZER_COLORS[tool] }} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-text-default">{OPTIMIZER_LABELS[tool]}</h3>
          <p className="text-xs text-text-muted">{OPTIMIZER_ROLES[tool]}</p>
        </div>
        <Button variant="text" className="!min-h-0 !p-0 text-xs" onClick={() => onSelect(tool)}>
          View details
        </Button>
      </div>
      {reporting ? (
        <>
          <div className="mt-3 flex items-center gap-3">
            <MiniGauge label={`${OPTIMIZER_LABELS[tool]} ${gauge.label.toLowerCase()}`} value={gauge.value} bands={gauge.bands} />
            <p className="text-xs text-text-muted">{gauge.label}</p>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
            {metrics.map((metric) => (
              <div key={metric.label}>
                <dt className="text-[11px] uppercase tracking-wide text-text-faint">{metric.label}</dt>
                <dd className="font-semibold tabular-nums text-text-default">{metric.value}</dd>
              </div>
            ))}
          </dl>
          {footnote && <p className="mt-3 text-[11px] text-text-faint">{footnote}</p>}
        </>
      ) : (
        <p className="mt-3 rounded-input border border-dashed border-border px-3 py-2 text-xs text-text-muted">
          Not reporting in this range. Open the screen for setup steps.
        </p>
      )}
    </div>
  );
}
